import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor as useDndSensor,
  useSensors as useDndSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { UserRole } from "common";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Heading, YStack, XStack, Spinner, Text } from "tamagui";

import {
  SensorErrorsCard,
  SensorAlertsCard,
  ActiveSensorsCard,
  SensorHealthCard,
  SensorDistributionCard,
  HVACRawDataCard,
} from "../../components/AdminAnalyticsCards";
import {
  WaterQualityCard,
  HVACFilterStatusCard,
  EnergySavingContent,
  TopLiveElectricalUsageCard,
  HumidityCard,
} from "../../components/AnalyticsCard";
import { useAppSync } from "../../contexts/AppSyncContext";
import { useUserRoleContext } from "../../contexts/UserRoleContext";

const HOMEOWNER_CARD_COMPONENTS = {
  water: WaterQualityCard,
  usage: TopLiveElectricalUsageCard,
  hvac: HVACFilterStatusCard,
  energy: EnergySavingContent,
  humidity: HumidityCard,
  seerRawData: HVACRawDataCard,
};

const ADMIN_CARD_COMPONENTS = {
  sensorErrors: SensorErrorsCard,
  sensorAlerts: SensorAlertsCard,
  activeSensors: ActiveSensorsCard,
  sensorHealth: SensorHealthCard,
  sensorDistribution: SensorDistributionCard,
};

const initialHomeownerOrder = [
  "water",
  "humidity",
  "energy",
  "usage",
  "hvac",
  "seerRawData",
];
const initialAdminOrder = [
  "sensorErrors",
  "sensorAlerts",
  "activeSensors",
  "sensorHealth",
  "sensorDistribution",
  "serviceRequests",
];

function chunk<T>(arr: T[], size: number): T[][] {
  const res: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    res.push(arr.slice(i, i + size));
  }
  return res;
}

function SortableCard({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  return (
    <div
      ref={setNodeRef}
      style={{
        flex: 1,
        minWidth: 0,
        display: "flex",
        opacity: isDragging ? 0.7 : 1,
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      {...attributes}
      {...listeners}
    >
      {children}
    </div>
  );
}

const Analytics = () => {
  const { sensors, sensorsLoading, sensorsError } = useAppSync();
  const { role } = useUserRoleContext();
  const { t } = useTranslation();

  const isAdmin = role === UserRole.ADMIN || role === UserRole.INSTALLER;
  const cardComponents = isAdmin
    ? ADMIN_CARD_COMPONENTS
    : HOMEOWNER_CARD_COMPONENTS;
  const initialOrder = isAdmin ? initialAdminOrder : initialHomeownerOrder;

  const [order, setOrder] = useState(initialOrder);

  const dndSensors = useDndSensors(
    useDndSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = order.indexOf(active.id);
      const newIndex = order.indexOf(over.id);
      setOrder(arrayMove(order, oldIndex, newIndex));
    }
  };

  const getRows = () => {
    if (isAdmin) {
      const kpiCards = order.slice(0, 3);
      const detailCards = order.slice(3);
      const detailRows = chunk(detailCards, 2);
      return [kpiCards, ...detailRows];
    }
    return chunk(order, 2);
  };

  const rows = getRows();

  if (sensorsLoading || !sensors || sensors.length === 0) {
    return (
      <YStack flex={1} justifyContent="center" alignItems="center" padding="$4">
        <Spinner size="large" />
      </YStack>
    );
  }

  if (sensorsError) {
    return (
      <YStack flex={1} justifyContent="center" alignItems="center" padding="$4">
        <Text color="$error" fontSize="$6">
          {sensorsError}
        </Text>
      </YStack>
    );
  }

  return (
    <YStack gap="$5">
      <YStack gap="$3">
        <Heading fontSize="$8" lineHeight="$6">
          {t("sidebar.analytics")}
        </Heading>
      </YStack>
      <DndContext
        sensors={dndSensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={order} strategy={rectSortingStrategy}>
          {rows.map((row, rowIdx) => (
            <XStack key={rowIdx} gap="$5" width="100%">
              {row.map((id) =>
                id ? (
                  <SortableCard key={id} id={id}>
                    {cardComponents[id]
                      ? React.createElement(cardComponents[id])
                      : null}
                  </SortableCard>
                ) : (
                  <div
                    key={`empty-${rowIdx}`}
                    style={{ flex: 1, minWidth: 0 }}
                  />
                ),
              )}
              {row.length < (rowIdx === 0 && isAdmin ? 3 : 2) && (
                <div style={{ flex: 1, minWidth: 0 }} />
              )}
            </XStack>
          ))}
        </SortableContext>
      </DndContext>
    </YStack>
  );
};

export default Analytics;
